import { FC } from 'react';
import classNames from 'classnames';
import styles from './healthtimeline.module.sass';

export type DotStatus = 'pass' | 'warn' | 'breach' | 'empty';

export interface TimelineDot {
  /** Status for this day */
  status: DotStatus;
  /** Optional date label */
  date?: string;
}

export type TimelineSize = 'default' | 'sm';

export interface HealthTimelineProps {
  /** Array of daily status dots (newest last) */
  dots: TimelineDot[];
  /** Size variant — "sm" for inline table use (8×8px dots, 3px gap, no labels) */
  size?: TimelineSize;
  /** Label for start date */
  startLabel?: string;
  /** Label for end date (default: "Today") */
  endLabel?: string;
  /** Additional CSS class */
  className?: string;
}

/**
 * HealthTimeline — compact dot strip showing daily covenant health.
 * Last dot gets a subtle glow to mark "today."
 *
 * @example
 * <HealthTimeline
 *   dots={Array(14).fill({ status: 'pass' }).map((d, i) => i === 4 ? { status: 'warn' } : d)}
 *   startLabel="Mar 20"
 *   endLabel="Today"
 * />
 */
export const HealthTimeline: FC<HealthTimelineProps> = ({
  dots,
  size = 'default',
  startLabel,
  endLabel = 'Today',
  className,
}) => {
  const showLabels = size === 'default' && (startLabel || endLabel);

  return (
    <div className={classNames(styles.wrap, size === 'sm' && styles.sm, className)}>
      <div className={styles.timeline}>
        {dots.map((dot, idx) => {
          const isToday = idx === dots.length - 1;
          return (
            <div
              key={idx}
              className={classNames(styles.dot, styles[dot.status], { [styles.today]: isToday })}
              title={dot.date || (isToday ? 'Today' : undefined)}
            />
          );
        })}
      </div>
      {showLabels && (
        <div className={styles.labels}>
          <span className={styles.label}>{startLabel}</span>
          <span className={styles.label}>{endLabel}</span>
        </div>
      )}
    </div>
  );
};

HealthTimeline.displayName = 'HealthTimeline';

export default HealthTimeline;
